import { DocumentTextIcon } from '@heroicons/react/24/outline';
import Avatar from './Avatar';
import CheckEventCell from './CheckEventCell';
import { NeedsApprovalWorker } from '../api/types';
import { upToTwoDecimals } from '../util/numberFormat';

type Props = {
  workers: NeedsApprovalWorker[];
  timezone?: string | null;
  onReview: (timesheetId: number) => void;
};

/**
 * The roster that expands under a Needs Approval shift row (GRAVY-10366).
 *
 * Inline under the row, not in a side panel — the ticket's AC describes the
 * pre-design screen, and D1 settled that the Figma is the bar. See §7.6 of the
 * plan.
 */
const NeedsApprovalWorkerTable = ({ workers, timezone, onReview }: Props) => {
  if (workers.length === 0) {
    return (
      <p className="dashv2-naworkers-empty" data-testid="dashv2-approval-workers-empty">
        No workers on this shift yet.
      </p>
    );
  }

  return (
    <div className="dashv2-naworkers" data-testid="dashv2-approval-workers">
      <div className="dashv2-naworkers-head" aria-hidden="true">
        <span>Worker</span>
        <span>Check-in</span>
        <span>Check-out</span>
        <span>Total hrs</span>
        <span className="dashv2-naworkers-spacer" />
      </div>

      {workers.map((w) => {
        // Any flagged event tints the WHOLE row, so ops can spot the worker in
        // a busy shift without reading every icon (AC).
        const rowFlagged = w.checkin.flagged || w.checkout.flagged;

        return (
          <div
            key={w.timesheet_id}
            className={`dashv2-naworker${rowFlagged ? ' is-flagged' : ''}`}
            data-testid={`dashv2-approval-worker-${w.timesheet_id}`}
          >
            <div className="dashv2-naworker-name">
              <Avatar src={w.avatar_url} name={w.worker_name} />
              <span>{w.worker_name || '—'}</span>
            </div>

            <CheckEventCell
              event={w.checkin}
              timezone={timezone}
              testId={`dashv2-approval-worker-${w.timesheet_id}-checkin`}
            />
            <CheckEventCell
              event={w.checkout}
              timezone={timezone}
              testId={`dashv2-approval-worker-${w.timesheet_id}-checkout`}
            />

            {/* Figma frame 39583-197886 shows "8.5 hrs" / "6 hrs": the unit is
                repeated in the cell, and there is no zero padding. `toFixed(2)`
                rendered a bare "8.50". */}
            <span
              className="dashv2-naworker-hours"
              data-testid={`dashv2-approval-worker-${w.timesheet_id}-hours`}
            >
              {w.total_hours == null ? '—' : `${upToTwoDecimals(w.total_hours)} hrs`}
            </span>

            {/* Every row has one, flagged or not: approval is per worker, and
                a clean timesheet still has to be reviewable (AC). */}
            <button
              type="button"
              className="dashv2-naworker-review"
              data-testid={`dashv2-approval-worker-${w.timesheet_id}-review`}
              onClick={() => onReview(w.timesheet_id)}
            >
              <DocumentTextIcon />
              Review
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default NeedsApprovalWorkerTable;
